import { AdditionalServicesService } from '@additional-services/services/additional-services.service';
import { GaEventService } from '@analytics/services/ga.event.service';
import { Component, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CompleteOrderService } from '@complete-order/services/complete-order.service';
import { BaseComponent } from '@core/components/base/base.component';
import {
  fadeInOut,
  Offers,
  OfferType,
  ReplacementOffer,
} from '@dgx-pr-spa/shared/util';
import { ProductDetailsService } from '@product-details/services/product-details.service';
import { ViewOffersService } from '@view-offers/services/view-offers.service';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-additional-services',
  templateUrl: './additional-services.component.html',
  styleUrls: ['./additional-services.component.scss'],
  animations: [fadeInOut],
})
export class AdditionalServicesComponent extends BaseComponent {
  constructor(
    public readonly viewOffersService: ViewOffersService,
    public readonly router: Router,
    public readonly route: ActivatedRoute,
    public readonly completeOrderService: CompleteOrderService,
    public readonly productDetailsService: ProductDetailsService,
    public readonly additionalServicesService: AdditionalServicesService,
    public readonly gaEventService: GaEventService
  ) {
    super();
  }

  /**
   * Receives complete offers data
   */
  @Input() offers: Offers;

  /**
   * Receives the selected offer
   */
  @Input() selected: ReplacementOffer;

  /**
   * Receives the offer reference
   */
  @Input() offerReference: string;

  /**
   * Internal boolean to track whether modal should be shown or not
   */
  showUpsellModal = false;

  /**
   * Observable boolean of installation selected state
   */
  isInstallationSelected$ =
    this.additionalServicesService.isInstallationSelected$;

  /**
   * Observable boolean of disposal selected state
   */
  isDisposalSelected$ = this.additionalServicesService.isDisposalSelected$;

  /**
   * Observable of the installation service data on the selected item
   */
  getInstallation$ = this.additionalServicesService.getInstallation$;

  /**
   * Observable of the disposal service data on the selected item
   */
  getDisposal$ = this.additionalServicesService.getDisposal$;

  /**
   * Observable of the delivery service data on the selected item
   */
  getDelivery$ = this.additionalServicesService.getDelivery$;

  /**
   * Observable of the delivery dates available
   */
  deliveryDates$ = this.productDetailsService.deliveryDates$;

  /**
   * Observable boolean of installation available state
   */
  isInstallationUpsellAvailable$ =
    this.additionalServicesService.isInstallationUpsellAvailable$;

  /**
   * Observable boolean of disposal available state
   */
  isDisposalUpsellAvailable$ =
    this.additionalServicesService.isDisposalUpsellAvailable$;

  /**
   * Observable boolean if any upsell is available state
   */
  isUpsellAvailable$ = this.additionalServicesService.isUpsellAvailable$;

  /**
   * Observable of the comparisons selected
   */
  comparisons$ = this.viewOffersService.comparisons$;

  /**
   * Observable of the montly plan selected
   */
  costSummary$ = this.completeOrderService.costSummary$;

  /**
   * enum of offer types
   */
  offerType = OfferType;

  /**
   * Scrolls into view
   */
  scrollIntoView(id: string, behavior?: ScrollBehavior) {
    document.getElementById(id).scrollIntoView({
      behavior,
      block: 'start',
      inline: 'nearest',
    });
  }

  /**
   * Response handler from upsell modal
   */
  onUpsellModalResponse(event: {
    scrollToExtras: boolean;
    upsellPopupAction: string;
  }) {
    this.showUpsellModal = false;
    this.gaEventService.AdditionalServicesPopUpClose({
      payload: {
        upsellPopupAction: event?.upsellPopupAction,
      },
    });

    if (event?.scrollToExtras) {
      this.scrollIntoView('extras', 'smooth');
    } else {
      this.navigateToNextPage();
    }
  }

  /**
   * Handle redirect to next page or open upsell modal if valid
   */
  handleUpsell(available) {
    if (available) {
      this.showUpsellModal = true;
      setTimeout(() => {
        this.gaEventService.AdditionalServicesPopUpShow();
      });
    } else {
      this.navigateToNextPage();
    }
  }

  navigateToNextPage(): void {
    if (this.selected.rrp <= 100) {
      this.router.navigate(['/complete-order'], {
        queryParamsHandling: 'preserve',
      });
    } else {
      this.router.navigate(['/protection'], {
        queryParams: { PRRef: this.offerReference },
      });
    }
  }

  /**
   * Continue click
   */
  continueClick() {
    this.gaEventService.AdditionalServicesContinueClick();
    this.isUpsellAvailable$.pipe(take(1)).subscribe((available) => {
      this.handleUpsell(available);
    });
  }
}
